import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, FileText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg, openPdf } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const NOTE_KINDS = [["progress", "Daily progress"], ["nursing", "Nursing note"],
  ["order", "Doctor order"], ["medication", "Medication chart"]];
const VITALS = [["bp", "BP"], ["pulse", "Pulse"], ["temperature", "Temp"], ["spo2", "SpO2"], ["rr", "RR"]];
const EMPTY_MED = { name: "", dose: "", morning: true, afternoon: false, night: true, duration: "5 days", instructions: "" };

export default function AdmissionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useApp();
  const [a, setA] = useState(null);
  const [note, setNote] = useState({ kind: "progress", vitals: {}, text: "" });
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [ds, setDs] = useState({
    diagnosis: "", treatment: "", hospital_course: "", investigations: "", advice: "",
    diet: "", followup: "", warnings: "", discharge_date: new Date().toISOString().slice(0, 10),
  });
  const [meds, setMeds] = useState([{ ...EMPTY_MED }]);

  const load = () => api.get(`/admissions/${id}`).then(({ data }) => {
    setA(data);
    if (data.discharge_summary) {
      setDs((s) => ({ ...s, ...data.discharge_summary }));
      setMeds(data.discharge_summary.medicines?.length ? data.discharge_summary.medicines : [{ ...EMPTY_MED }]);
    } else {
      setDs((s) => ({ ...s, diagnosis: data.diagnosis || "" }));
    }
  });
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); /* eslint-disable-next-line */ }, [id]);

  const addNote = async () => {
    if (!note.text.trim()) return toast.error("Write the note text");
    try {
      await api.post(`/admissions/${id}/notes`, note);
      toast.success("Note added");
      setNote({ kind: note.kind, vitals: {}, text: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doDischarge = async () => {
    try {
      await api.post(`/admissions/${id}/discharge`, { ...ds, medicines: meds.filter((m) => m.name.trim()) });
      toast.success("Patient discharged — summary saved");
      setDischargeOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!a) return <p className="text-sm text-muted-foreground">Loading inpatient chart…</p>;
  const canDoctor = ["super_admin", "doctor"].includes(user.role);

  return (
    <div className="space-y-5" data-testid="admission-detail-page">
      <Button variant="ghost" size="sm" onClick={() => nav("/admissions")} className="gap-2 -ml-2">
        <ArrowLeft className="h-4 w-4" /> Back to admissions
      </Button>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary">{a.ip_number}</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{a.patient_name}</h1>
          <div className="text-sm text-muted-foreground flex items-center gap-1.5 flex-wrap">
            <span>
              {a.patient_uhid} · {a.patient_age} yrs · {a.patient_gender} · {a.ward} / {a.room || "—"} / {a.bed || "—"}
            </span>
            <Badge variant={a.status === "Admitted" ? "default" : "secondary"}>{a.status}</Badge>
          </div>
        </div>
        <div className="flex gap-2">
          {a.discharge_summary && (
            <Button variant="outline" className="gap-2" data-testid="discharge-summary-pdf-button"
              onClick={() => openPdf(`/admissions/${id}/discharge-summary/pdf`)}>
              <FileText className="h-4 w-4" /> Discharge summary PDF
            </Button>
          )}
          {canDoctor && (
            <Button onClick={() => setDischargeOpen(true)} data-testid="discharge-button">
              {a.status === "Discharged" ? "Edit discharge summary" : "Discharge patient"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="shadow-sm lg:col-span-1">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Add chart entry</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Entry type</Label>
              <Select value={note.kind} onValueChange={(v) => setNote({ ...note, kind: v })}>
                <SelectTrigger data-testid="note-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NOTE_KINDS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {VITALS.map(([k, l]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-[10px]">{l}</Label>
                  <Input className="h-8 text-xs" value={note.vitals[k] || ""} data-testid={`note-vital-${k}`}
                    onChange={(e) => setNote({ ...note, vitals: { ...note.vitals, [k]: e.target.value } })} />
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Note</Label>
              <Textarea rows={4} value={note.text} data-testid="note-text-input"
                onChange={(e) => setNote({ ...note, text: e.target.value })} />
            </div>
            <Button className="w-full gap-2" onClick={addNote} data-testid="add-note-button">
              <Plus className="h-4 w-4" /> Add to chart
            </Button>
          </CardContent>
        </Card>

        <Card className="shadow-sm lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Inpatient chart</CardTitle></CardHeader>
          <CardContent>
            <Tabs defaultValue="all">
              <TabsList>
                <TabsTrigger value="all" data-testid="chart-tab-all">All</TabsTrigger>
                {NOTE_KINDS.map(([k, l]) => (
                  <TabsTrigger key={k} value={k} data-testid={`chart-tab-${k}`}>{l}</TabsTrigger>
                ))}
              </TabsList>
              {["all", ...NOTE_KINDS.map(([k]) => k)].map((tab) => {
                const list = tab === "all" ? a.notes : a.notes.filter((n) => n.kind === tab);
                return (
                  <TabsContent key={tab} value={tab} className="space-y-3 max-h-[420px] overflow-y-auto pt-3">
                    {list.length === 0 && <p className="text-sm text-muted-foreground">No entries yet.</p>}
                    {list.map((n) => (
                      <div key={n.id} className="border-l-2 border-primary pl-3" data-testid={`note-${n.id}`}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] capitalize">{n.kind}</Badge>
                          <span className="text-xs font-mono text-muted-foreground">
                            {new Date(n.created_at).toLocaleString()} · {n.author_name}
                          </span>
                        </div>
                        {Object.values(n.vitals || {}).some(Boolean) && (
                          <p className="text-xs font-mono text-muted-foreground mt-0.5">
                            {Object.entries(n.vitals).filter(([, v]) => v)
                              .map(([k, v]) => `${k.toUpperCase()} ${v}`).join(" · ")}
                          </p>
                        )}
                        <p className="text-sm whitespace-pre-wrap mt-0.5">{n.text}</p>
                      </div>
                    ))}
                  </TabsContent>
                );
              })}
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Investigations during stay</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {a.investigations.length === 0 && (
            <p className="text-sm text-muted-foreground">
              None linked. Order from the Investigations module and link this admission.
            </p>
          )}
          {a.investigations.map((i) => (
            <div key={i.id} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5">
              <span>{i.category} · {i.name}</span>
              <Badge variant="outline">{i.status}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={dischargeOpen} onOpenChange={setDischargeOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="discharge-dialog">
          <DialogHeader><DialogTitle className="font-heading">Discharge summary</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["diagnosis", "Final diagnosis"], ["treatment", "Treatment given"],
              ["hospital_course", "Hospital course"], ["investigations", "Investigations"],
              ["advice", "Advice on discharge"], ["diet", "Diet"], ["followup", "Follow up"],
              ["warnings", "Warning signs"]].map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs">{label}</Label>
                <Textarea rows={2} value={ds[k] || ""} data-testid={`discharge-${k}-input`}
                  onChange={(e) => setDs({ ...ds, [k]: e.target.value })} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-xs">Discharge date</Label>
              <Input type="date" value={ds.discharge_date}
                onChange={(e) => setDs({ ...ds, discharge_date: e.target.value })} />
            </div>
          </div>

          <div className="space-y-2 pt-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Discharge medication</Label>
              <Button size="sm" variant="outline" className="gap-1.5" data-testid="add-discharge-med-button"
                onClick={() => setMeds([...meds, { ...EMPTY_MED }])}>
                <Plus className="h-3.5 w-3.5" /> Add
              </Button>
            </div>
            {meds.map((m, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`discharge-med-${i}`}>
                <div className="col-span-4 space-y-1"><Label className="text-[10px]">Medicine</Label>
                  <Input value={m.name} data-testid={`discharge-med-name-${i}`}
                    onChange={(e) => setMeds(meds.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></div>
                <div className="col-span-2 space-y-1"><Label className="text-[10px]">Dose</Label>
                  <Input value={m.dose}
                    onChange={(e) => setMeds(meds.map((x, j) => j === i ? { ...x, dose: e.target.value } : x))} /></div>
                <div className="col-span-2 space-y-1"><Label className="text-[10px]">M / A / N</Label>
                  <div className="flex items-center gap-2 h-9">
                    {["morning", "afternoon", "night"].map((t) => (
                      <Checkbox key={t} checked={!!m[t]}
                        onCheckedChange={(v) => setMeds(meds.map((x, j) => j === i ? { ...x, [t]: !!v } : x))} />
                    ))}
                  </div></div>
                <div className="col-span-2 space-y-1"><Label className="text-[10px]">Duration</Label>
                  <Input value={m.duration}
                    onChange={(e) => setMeds(meds.map((x, j) => j === i ? { ...x, duration: e.target.value } : x))} /></div>
                <div className="col-span-1 space-y-1"><Label className="text-[10px]">Notes</Label>
                  <Input value={m.instructions}
                    onChange={(e) => setMeds(meds.map((x, j) => j === i ? { ...x, instructions: e.target.value } : x))} /></div>
                <Button size="icon" variant="ghost" onClick={() => setMeds(meds.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDischargeOpen(false)}>Cancel</Button>
            <Button onClick={doDischarge} data-testid="save-discharge-button">Save & discharge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
